import { useAuth0 } from "@auth0/auth0-react";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { PropTypes } from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { constantsData, identifiersData, inputData, permissionsData } from "@/data";
import { useEditVideo } from "@/hooks";
import { StyledTextField } from "@/styles";
import { canDo } from "@/utils";

const ReleaseVideoIsrcForm = ({ isrc, trackId, videoId }) => {
    const [isEditing, setIsEditing] = useState(false);

    const [value, setValue] = useState(isrc ?? "");

    const [error, setError] = useState(null);

    const isrcInputRef = useRef(null);

    const { t } = useTranslation();

    const { user } = useAuth0();

    const { editVideoIsrc, loading } = useEditVideo(trackId);

    const { mediaEdit: mediaEditPermission, mediaRead: mediaReadPermission } = permissionsData.permissions.keys;

    const canEdit = canDo(user, [mediaEditPermission, mediaReadPermission]);

    const hasInvalidLength = value.length !== 12;

    const isDirty = value !== (isrc ?? "");

    useEffect(() => {
        setValue(isrc ?? "");

        setError(null);

        setIsEditing(false);
    }, [isrc]);

    // move the caret into the field once it is opened from the edit icon
    useEffect(() => {
        if (isEditing) isrcInputRef.current?.focus();
    }, [isEditing]);

    const closeHandler = () => {
        setValue(isrc ?? "");

        setError(null);

        setIsEditing(false);
    };

    const saveHandler = async () => {
        if (hasInvalidLength) {
            setError(t("messages.inputMsgs.length", { length: 12 }));

            return;
        }

        setError(null);

        const isUpdated = await editVideoIsrc(videoId, value, (err) =>
            setError(
                err.message === constantsData.duplicatedVideoIsrc
                    ? t("messages.videoMsgs.duplicatedIsrc")
                    : err.message,
            ),
        );

        // keep the input open on failure so the value can be corrected
        if (isUpdated) setIsEditing(false);
    };

    if (!isEditing) {
        return (
            <Box
                sx={{
                    alignItems: "center",
                    display: "flex",
                    gap: 0.5,
                }}
            >
                <Typography
                    id={`${inputData.names.isrc}_${identifiersData.ids.label}_${videoId}`}
                    variant="caption"
                    sx={{
                        ...(!isrc && { fontStyle: "italic", opacity: 0.6 }),
                    }}
                >
                    {isrc ? `${t("labels.isrc")}: ${isrc}` : t("messages.videoMsgs.missingIsrc")}
                </Typography>
                {canEdit && (
                    <Tooltip title={t("actions.edit")}>
                        <IconButton
                            id={`${inputData.names.isrc}_${identifiersData.ids.button}_${videoId}`}
                            size="small"
                            onClick={() => setIsEditing(true)}
                        >
                            <EditOutlinedIcon fontSize="inherit" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        );
    }

    return (
        <Box
            sx={{
                alignItems: "flex-start",
                display: "flex",
                gap: 0.5,
            }}
        >
            <StyledTextField
                inputRef={isrcInputRef}
                disabled={loading}
                error={!!error}
                helperText={error}
                id={`${inputData.names.isrc}_${identifiersData.ids.input}_${videoId}`}
                label={t("labels.isrc")}
                size="small"
                value={value}
                variant="outlined"
                onChange={(e) => {
                    setValue(e.target.value);

                    setError(null);
                }}
            />
            <Tooltip title={t("actions.save")}>
                <span>
                    <IconButton
                        disabled={!isDirty || hasInvalidLength || loading}
                        id={`${inputData.names.isrc}_${identifiersData.ids.button}_save_${videoId}`}
                        size="small"
                        onClick={saveHandler}
                    >
                        <SaveOutlinedIcon fontSize="inherit" />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title={t("actions.cancel")}>
                <span>
                    <IconButton
                        disabled={loading}
                        id={`${inputData.names.isrc}_${identifiersData.ids.button}_cancel_${videoId}`}
                        size="small"
                        onClick={closeHandler}
                    >
                        <CloseIcon fontSize="inherit" />
                    </IconButton>
                </span>
            </Tooltip>
        </Box>
    );
};

export default ReleaseVideoIsrcForm;

ReleaseVideoIsrcForm.propTypes = {
    isrc: PropTypes.string,
    trackId: PropTypes.number,
    videoId: PropTypes.number,
};
